import { Box, Link, TextField } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import type { CustomerListItemDto } from '@app/shared';
import { DataTable, rowNumber } from '../../components/DataTable';
import type { Column } from '../../components/DataTable';
import { FilterPanel } from '../../components/FilterPanel';
import { PageHeader } from '../../components/PageHeader';
import { StatusChip } from '../../components/StatusChip';
import { useListState } from '../../lib/useListState';
import { useRecentSearches } from './api';
import { CustomerRowLinks, customerPath, yesNo } from './parts';

const NO_FILTERS = { dateFrom: '', dateTo: '' };

/**
 * Recent Searches (PC295): "CSR Search History", the customers this user opened, latest
 * first. A row opens the profile again, like a row of the search results.
 */
export function RecentSearchesPage() {
  const list = useListState(NO_FILTERS, 10);
  const history = useRecentSearches(list.params);

  const columns: Column<CustomerListItemDto>[] = [
    { key: '#', header: '#', width: 50, render: (_c, i) => rowNumber(i, list.page, list.pageSize) },
    { key: 'carrier', header: 'Carrier', render: (c) => c.carrier },
    {
      key: 'customerId',
      header: 'Customer ID',
      render: (c) => (
        <Link component={RouterLink} to={customerPath(c.id)} data-customer-id={c.id}>
          {c.customerId}
        </Link>
      ),
    },
    { key: 'accountNo', header: 'Account No', render: (c) => c.accountNo },
    { key: 'enrollmentId', header: 'Enrollment ID', render: (c) => c.enrollmentId },
    { key: 'mdn', header: 'Telephone', nowrap: true, render: (c) => c.mdn },
    { key: 'sim', header: 'SIM/ ESN Number', nowrap: true, render: (c) => c.sim },
    { key: 'isEsim', header: 'Is eSIM', render: (c) => yesNo(c.isEsim) },
    { key: 'firstName', header: 'First Name', render: (c) => c.firstName },
    { key: 'lastName', header: 'Last Name', render: (c) => c.lastName },
    { key: 'serviceAddress', header: 'Service Address', render: (c) => c.serviceAddress },
    { key: 'city', header: 'City', render: (c) => c.city },
    { key: 'state', header: 'State', render: (c) => c.state },
    { key: 'zip', header: 'ZIP Code', render: (c) => c.zip },
    { key: 'status', header: 'Status', render: (c) => <StatusChip status={c.status} /> },
  ];

  return (
    <Box>
      <PageHeader title="CSR Search History" code="PC295" />
      <FilterPanel onSearch={list.search} onReset={list.reset}>
        <TextField
          type="date"
          label="From"
          value={list.draft.dateFrom}
          onChange={(e) => list.setField('dateFrom', e.target.value)}
          slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: list.draft.dateTo } }}
        />
        <TextField
          type="date"
          label="To"
          value={list.draft.dateTo}
          onChange={(e) => list.setField('dateTo', e.target.value)}
          slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: list.draft.dateFrom } }}
        />
      </FilterPanel>

      <CustomerRowLinks>
        <DataTable
          label="CSR Search History"
          columns={columns}
          rows={history.data?.data}
          rowKey={(c) => c.id}
          loading={history.isFetching}
          error={history.error}
          onRetry={() => void history.refetch()}
          page={list.page}
          pageSize={list.pageSize}
          total={history.data?.total ?? 0}
          onPageChange={list.setPage}
          onPageSizeChange={list.setPageSize}
          pageSizeOptions={[10, 20, 50, 100]}
        />
      </CustomerRowLinks>
    </Box>
  );
}
